import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/services/apiClient';

function resolvePhotoRequestUrl(
  patientId: number | string,
  photoUrl?: string | null,
): string {
  const fallback = `/patients/${encodeURIComponent(String(patientId))}/photo`;
  const raw = String(photoUrl ?? '').trim();

  if (raw === '') {
    return fallback;
  }

  try {
    const parsed = new URL(raw, window.location.origin);
    const relative = `${parsed.pathname}${parsed.search}`;
    return relative.startsWith('/api/v5/') ? relative.slice('/api/v5'.length) : relative;
  } catch {
    return fallback;
  }
}

async function fetchPhotoBlobUrl(url: string): Promise<string | null> {
  const response = await apiClient.get<{ contentType?: string; data?: string } | Blob>(url, {
    headers: {
      Accept: 'image/*',
    },
  });

  const raw = response.data;

  if (raw instanceof Blob) {
    if (raw.size === 0) return null;
    const blobType = String(raw.type ?? '').toLowerCase();
    if (blobType !== '' && !blobType.startsWith('image/')) return null;
    return URL.createObjectURL(raw);
  }

  if (raw && typeof raw === 'object' && typeof raw.data === 'string' && typeof raw.contentType === 'string') {
    try {
      const bytes = Uint8Array.from(atob(raw.data), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: raw.contentType });
      if (blob.size === 0) return null;
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Fetches a patient's photo and exposes it as a cached object URL.
 *
 * `photoUrl` is tri-state, and the distinction is what keeps the Patients list
 * from firing one failed request per row (SCRUM-1648):
 *   - `null`      → the server said there is no photo. The query is disabled;
 *                   the caller renders its placeholder with NO request.
 *   - `undefined` → availability unknown (a caller that only has an id). The
 *                   legacy by-id route is fetched; the API answers 204 for
 *                   "no photo", which resolves to `undefined` here.
 *   - a string    → the URL to fetch (an `/api/v5/...` prefix is stripped).
 *
 * Callers must therefore NOT write `photoUrl ?? null` when forwarding a
 * possibly-undefined value — that turns "unknown" into "known none" and hides
 * a real photo. Pass it through unchanged.
 *
 * Backed by React Query so revisits to the same patient (e.g. via the recent
 * patient sidebar) reuse the previously decoded blob instead of re-fetching.
 * The blob URL is held in the React Query cache and freed automatically when
 * the cache entry is garbage collected.
 */
export function usePatientPhoto(
  patientId: number | string | null | undefined,
  photoUrl?: string | null,
): string | undefined {
  const token = useAuthStore((s) => s.token);
  const accountId = useAuthStore((s) => s.accountId);

  const query = useQuery({
    queryKey: ['patient-photo', patientId, photoUrl ?? null, accountId],
    queryFn: () => fetchPhotoBlobUrl(resolvePhotoRequestUrl(patientId!, photoUrl)),
    // `photoUrl === null` is "known none": never ask the server for it.
    enabled: !!patientId && !!token && photoUrl !== null,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
  });

  return query.data ?? undefined;
}
